import React, {type CSSProperties} from 'react';
import classNames from 'classnames';
import memoizeOne from 'memoize-one';

import * as Utils from 'utils/utils';
import {showMobileSubMenuModal} from 'actions/global_actions';

import type {Menu} from 'types/store/plugins';

import Constants from 'utils/constants';
import {type FileInfo} from '@mattermost/types/files';
import {type MenuContextType, MenuContext} from '../menu_context';

import './menu_item.scss';
import {hideMenu} from 'components/widgets/context-menu';
import {CheckIcon, ChevronRightIcon} from '@time-webkit/all/icons';

// Requires an object conforming to a submenu structure passed to registerPostDropdownSubMenuAction
// of the form:
// {
//     "id": "A",
//     "parentMenuId": null,
//     "text": "A text",
//     "subMenu": [
//         {
//             "id": "B",
//             "parentMenuId": "A",
//             "text": "B text"
//             "subMenu": [],
//             "action": () => {},
//             "filter": () => {},
//         }
//     ],
//     "action": () => {},
//     "filter": () => {},
// }
// Submenus can contain Submenus as well

type CreatePostSubmenuProps = {
    getPostText: () => string;
    getPostFiles: () => FileInfo[];
    clearPost: () => void;
}

export type Props = {
    itemClassName?: string;
    id?: string;
    actionProps?: string | CreatePostSubmenuProps;
    postId?: string;
    text: React.ReactNode;
    selectedValueText?: React.ReactNode;
    renderSelected?: boolean;
    subMenu?: Menu[];
    subMenuClass?: string;
    icon?: React.ReactNode;
    action?: (props?: Props['actionProps'] | Props['postId'] | React.SyntheticEvent) => void;
    filter?: (id?: string) => boolean;
    ariaLabel?: string;
    root?: boolean;
    show?: boolean;
    direction?: 'left' | 'right';
    openUp?: boolean;
    styleSelectableItem?: boolean;
    extraText?: string;
    rightDecorator?: React.ReactNode;
    isHeader?: boolean;
    extraBottomOffset?: number;
    hoverDisabled?: boolean;
    preventClose?: boolean;
}

type State = Pick<MenuContextType, 'openedSubMenuItem'> & {
    top?: number;
}

const hoverDisabledStyles = {
    pointerEvents: 'none',
};

const defaultStyles = {};

export default class SubMenuItem extends React.PureComponent<Props, State> {
    declare context: React.ContextType<typeof MenuContext>

    private node: React.RefObject<HTMLLIElement>;
    private ref: React.RefObject<HTMLUListElement>;
    private itemId = Symbol('SubMenuItem');
    private isMobile = Utils.isMobile();

    static contextType = MenuContext;

    public static defaultProps = {
        show: true,
        direction: 'left',
        subMenuClass: 'pl-4',
        renderSelected: true,
    };

    public constructor(props: Props) {
        super(props);
        this.node = React.createRef();
        this.ref = React.createRef();

        this.state = {
            top: undefined,
            openedSubMenuItem: null,
        };
    }

    componentDidUpdate() {
        if (!this.props.subMenu) {
            return;
        }
        const menu = this.ref.current;
        if (!menu) {
            return;
        }

        // Wait for component positioning
        requestAnimationFrame(() => {
            const {bottom, height} = menu.getBoundingClientRect();

            if (bottom > window.innerHeight && this.state.top === undefined) {
                const extraBottomOffset = this.props.extraBottomOffset || 0;
                const top = window.innerHeight - extraBottomOffset - height;
                this.setState({top}); // eslint-disable-line react/no-did-update-set-state
            }
        });
    }

    show = () => {
        this.setState({openedSubMenuItem: null});
        this.context.onOpenSubMenuItem(this.itemId);
    }

    hide = () => {
        this.setState({openedSubMenuItem: null});
        this.context.onCloseSubMenuItem();
    }

    private getContextValue = memoizeOne((openedSubMenuItem: symbol | null, isShown: boolean): MenuContextType => {
        return {
            isParentMenuShown: isShown,
            openedSubMenuItem,
            onOpenSubMenuItem: (item) => this.setState({openedSubMenuItem: item}),
            onCloseSubMenuItem: () => this.setState({openedSubMenuItem: null}),
        };
    })

    private getSubmenuStyles = (isShown: boolean): React.CSSProperties => {
        const subMenuStyle: CSSProperties = {position: 'fixed', visibility: 'hidden'};
        const isVisible = isShown && this.props.subMenu?.length && !this.isMobile;

        if (isVisible && this.node.current && this.ref.current) {
            const parentBounds = this.node.current.parentElement!.getBoundingClientRect();

            subMenuStyle.visibility = 'visible';
            subMenuStyle.top = this.state.top ?? this.node.current.getBoundingClientRect().top - 7;

            const subMenuHeight = this.ref.current.getBoundingClientRect().height;

            if (subMenuStyle.top + subMenuHeight >= document.body.clientHeight - 8) {
                subMenuStyle.top = document.body.clientHeight - 8 - subMenuHeight;
            }

            const setStyleForRight = () => {
                if (document.body.clientWidth < parentBounds.right + this.ref.current!.clientWidth) {
                    setStyleForLeft();
                    return;
                }
                subMenuStyle.left = parentBounds.right - 3;
            };

            const setStyleForLeft = () => {
                subMenuStyle.left = parentBounds.left - (this.ref.current!.offsetWidth - 3);
            };

            if (this.props.direction === 'left') {
                setStyleForLeft();
            } else {
                setStyleForRight();
            }
        }

        return subMenuStyle;
    }

    private onClick = (event: React.SyntheticEvent<HTMLElement>) => {
        event.preventDefault();
        const {id, actionProps, postId, subMenu, action, root, isHeader} = this.props;
        if (isHeader) {
            event.stopPropagation();
            return;
        }
        if (this.isMobile) {
            if (subMenu && subMenu.length) { // if contains a submenu, call openModal with it
                if (!root) { //required to close only the original menu
                    event.stopPropagation();
                }
                showMobileSubMenuModal(subMenu, actionProps, postId);
            } else if (action) { // leaf node in the tree handles action only
                action(actionProps || postId || event);
            }
        } else if (event.currentTarget.id === id && action) {
            action(actionProps || postId || event);
        }
        if (!this.props.preventClose) {
            hideMenu();
        }
    }

    handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (Utils.isKeyPressed(event, Constants.KeyCodes.ENTER)) {
            if (this.props.action) {
                this.onClick(event);
            } else {
                this.show();
            }
        }

        if (Utils.isKeyPressed(event, Constants.KeyCodes.RIGHT)) {
            if (this.props.direction === 'right') {
                this.show();
            } else {
                this.hide();
            }
        }

        if (Utils.isKeyPressed(event, Constants.KeyCodes.LEFT)) {
            if (this.props.direction === 'left') {
                this.show();
            } else {
                this.hide();
            }
        }
    }

    public render() {
        const {id, actionProps, postId, text, selectedValueText, subMenu, icon, filter, ariaLabel, styleSelectableItem, extraText, renderSelected} = this.props;

        if (filter && !filter(id)) {
            return '';
        }

        const hasSubmenu = subMenu && subMenu.length;
        const isShown = this.context.isParentMenuShown && this.context.openedSubMenuItem === this.itemId;

        let subMenuContent: React.ReactNode = '';

        if (!this.isMobile) {
            subMenuContent = (
                <ul
                    className={classNames(['a11y__popup Menu SubMenu Menu__content', {styleSelectableItem}])}
                    style={this.getSubmenuStyles(isShown)}
                    ref={this.ref}
                >
                    {hasSubmenu ? subMenu!.map((s, i) => {
                        if (s.isDivider) {
                            return <div key={`divider-${i}`} className='MenuGroup menu-divider' />;
                        }

                        const rightDecorator = s.text === selectedValueText ? (
                            <CheckIcon width={16} height={16} className='MenuItem__checkmark' />
                        ) : s.rightDecorator;

                        return (
                            <span
                                className='SubMenuItemContainer'
                                key={s.id}
                                style={s.hoverDisabled ? hoverDisabledStyles : defaultStyles}
                            >
                                <MenuContext.Provider value={this.getContextValue(this.state.openedSubMenuItem, isShown)}>
                                    <SubMenuItem
                                        id={s.id}
                                        actionProps={actionProps}
                                        postId={postId}
                                        text={s.text}
                                        selectedValueText={s.selectedValueText}
                                        icon={s.icon}
                                        subMenu={s.subMenu}
                                        hoverDisabled={s.hoverDisabled}
                                        action={s.action}
                                        filter={s.filter}
                                        ariaLabel={ariaLabel}
                                        root={false}
                                        direction={s.direction}
                                        isHeader={s.isHeader}
                                        rightDecorator={rightDecorator}
                                        itemClassName={s.itemClassName}
                                    />
                                </MenuContext.Provider>

                            </span>
                        );
                    }) : ''}
                </ul>
            );
        }

        const rightDecorator = hasSubmenu ? (
            <ChevronRightIcon width={16} height={16} className='MenuItem__chevron'/>
        ) : this.props.rightDecorator;

        return (
            <li
                className={classNames(
                    'SubMenuItem MenuItem',
                    this.props.itemClassName,
                    {
                        styleSelectableItem,
                        'submenu-item__selected': isShown && hasSubmenu,
                        'submenu-item__header': this.props.isHeader,

                    },
                )}
                role='menuitem'
                id={id + '_menuitem'}
                ref={this.node}
                onClick={this.onClick}
                style={this.props.hoverDisabled ? hoverDisabledStyles : defaultStyles}
            >
                <div
                    className={classNames([{styleSelectableItemDiv: styleSelectableItem}])}
                    id={id}
                    aria-label={ariaLabel}
                    onMouseEnter={this.show}
                    onClick={this.onClick}
                    tabIndex={0}
                    onKeyDown={this.handleKeyDown}
                >
                    <div className='MenuItem__content'>
                        {icon && <div className={'MenuItem__icon-container'}>{icon}</div>}
                        {text && (
                            <div className='MenuItem__text'>
                                <div className='MenuItem__primary-text'>{text}</div>
                                {extraText && <span className='MenuItem__help-text'>{extraText}</span>}
                            </div>
                        )}
                        {rightDecorator && (
                            <div className='MenuItem__right-decorator'>
                                {renderSelected && selectedValueText && (
                                    <span className='selected'>{selectedValueText}</span>
                                )}
                                {rightDecorator}
                            </div>
                        )}
                    </div>
                    {subMenuContent}
                </div>
            </li>
        );
    }
}
